/**
 * History page component
 */
import {
  Add,
  Delete,
  Edit,
  Terrain,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { FieldDetail } from '@/api/fields';
import { fieldsApi } from '@/api/fields';
import { fieldDetailToFormValues } from '@/components/Fields/FieldFormModal';
import { FieldLocationCard } from '@/components/Fields/FieldLocationCard';
import { LayersList } from '@/components/Layers/LayersList';
import { WellsList } from '@/components/Wells/WellsList';
import { useHistory } from '@/contexts/HistoryContext';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { countFieldWellsByType } from '@/utils/fieldWells';
import { buildFieldProductionChartData } from '@/utils/layerProductionCharts';
import { formatThousandVolumeTooltip, SCENARIO_UNIT_TONS } from '@/utils/scenarioUnits';

export const HistoryPage: React.FC = () => {
  useRegisterPageHelp('History');
  const { selectedDepositId, setSelectedDepositId, fieldsVersion, refreshFields, openCreateFieldForm, openEditFieldForm, registerFieldSavedHandler } = useHistory();
  const [deposits, setDeposits] = useState<Record<string, { name: string; type: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldDetail, setFieldDetail] = useState<FieldDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    registerFieldSavedHandler(() => {
      if (selectedDepositId) {
        fieldsApi.getFieldDetail(selectedDepositId).then(setFieldDetail);
      }
    });
    return () => registerFieldSavedHandler(null);
  }, [registerFieldSavedHandler, selectedDepositId]);

  // Load deposits metadata for empty-state checks (list lives in DepositsSidebar).
  useEffect(() => {
    const loadDeposits = async () => {
      try {
        setLoading(true);
        setError(null);
        const fieldsList = await fieldsApi.getFieldsList();

        const depositsMap: Record<string, { name: string; type: string }> = {};
        fieldsList.fields.forEach(field => {
          depositsMap[field.id] = {
            name: field.name,
            type: field.type
          };
        });

        setDeposits(depositsMap);
      } catch (err) {
        console.error('Error loading deposits:', err);
        setError(err instanceof Error ? err.message : 'Failed to load deposits');
      } finally {
        setLoading(false);
      }
    };

    loadDeposits();
  }, [fieldsVersion]);

  // Load field detail when selectedDepositId changes
  useEffect(() => {
    const loadFieldDetail = async () => {
      if (!selectedDepositId) {
        setFieldDetail(null);
        return;
      }

      try {
        setDetailLoading(true);
        setDetailError(null);
        setFieldDetail(null);
        const detail = await fieldsApi.getFieldDetail(selectedDepositId);
        setFieldDetail(detail);
      } catch (err) {
        console.error('Error loading field detail:', err);
        setDetailError(err instanceof Error ? err.message : 'Failed to load field details');
        setFieldDetail(null);
      } finally {
        setDetailLoading(false);
      }
    };

    loadFieldDetail();
  }, [selectedDepositId]);

  const fieldChartData = useMemo(
    () => buildFieldProductionChartData(fieldDetail?.production_data ?? []),
    [fieldDetail?.production_data],
  );

  const hasFieldOil = fieldChartData.some((row) => row.oil != null);
  const hasFieldGas = fieldChartData.some((row) => row.gas != null);
  const hasFieldWater = (fieldDetail?.production_data ?? []).some((row) => row.water != null);
  const hasFieldLiquid = hasFieldWater && fieldChartData.some((row) => row.liquid != null);
  const hasFieldWaterCut = fieldChartData.some((row) => row.waterCut != null);
  const showFieldProductivityChart = hasFieldOil || hasFieldGas || hasFieldLiquid || hasFieldWaterCut;

  const calculateCumulativeProduction = (productionData: FieldDetail['production_data']) => {
    const totals = { oil: 0, gas: 0 };
    productionData.forEach(data => {
      totals.oil += data.oil || 0;
      totals.gas += data.gas || 0;
    });
    return totals;
  };

  const getWellTypesCounts = countFieldWellsByType;

  const handleOpenEdit = async () => {
    if (!fieldDetail) {
      return;
    }
    try {
      const typesResponse = await fieldsApi.getFieldTypes();
      const typeMatch = typesResponse.types.find((t) => t.name === fieldDetail.type);
      if (!typeMatch) {
        setDetailError('Could not resolve field type for editing');
        return;
      }
      openEditFieldForm(fieldDetailToFormValues(fieldDetail, typeMatch.id));
    } catch (err) {
      console.error('Failed to prepare field edit:', err);
      setDetailError('Failed to open edit form');
    }
  };

  const handleDeleteField = async () => {
    if (!selectedDepositId) {
      return;
    }
    setDeleteLoading(true);
    try {
      await fieldsApi.deleteField(selectedDepositId);
      setDeleteDialogOpen(false);
      setFieldDetail(null);
      setSelectedDepositId(null);
      refreshFields();
    } catch (err) {
      console.error('Failed to delete field:', err);
      setDetailError('Failed to delete field');
    } finally {
      setDeleteLoading(false);
    }
  };

  const currentDeposit = selectedDepositId ? deposits[selectedDepositId] : null;
  const hasDeposits = Object.keys(deposits).length > 0;
  const displayName = fieldDetail?.name ?? currentDeposit?.name;
  const displayType = fieldDetail?.type ?? currentDeposit?.type;

  // Initial load before sidebar picks a default field.
  if (!selectedDepositId && loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loading && !selectedDepositId && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Loading deposits...</Typography>
        </Box>
      )}

      {error && !loading && !selectedDepositId && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Error loading deposits</Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && !hasDeposits && (
        <Box sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
            History
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No fields yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the + button in the Deposits sidebar or click below to create your first field.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateFieldForm}>
            Add field
          </Button>
        </Box>
      )}

      {!loading && !error && hasDeposits && selectedDepositId && displayName ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Terrain sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                  {displayName}
                </Typography>
                <Typography variant="body1" color="text.secondary" component="div">
                  Calculation history for {(displayType ?? 'field').toUpperCase()} deposit
                </Typography>
              </Box>
            </Box>
            {fieldDetail?.can_manage && (
              <Box>
                <MuiTooltip title="Edit field">
                  <IconButton color="primary" onClick={handleOpenEdit}>
                    <Edit />
                  </IconButton>
                </MuiTooltip>
                <MuiTooltip title="Delete field">
                  <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}>
                    <Delete />
                  </IconButton>
                </MuiTooltip>
              </Box>
            )}
          </Box>

          {/* Field detail summary */}
          <Box sx={{ mb: 3 }}>
            {detailLoading && (
              <Typography variant="body2" color="text.secondary">Loading field details...</Typography>
            )}
            {detailError && !detailLoading && (
              <Typography variant="body2" color="error">{detailError}</Typography>
            )}
            {!detailLoading && !detailError && fieldDetail && (
              <>
                                {/* Field Information Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {/* Cumulative Production Card */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 140 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Cumulative Production
                        </Typography>
                        {fieldDetail.production_data && fieldDetail.production_data.length > 0 ? (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">Oil:</Typography>
                              <Typography variant="body2" color="success.main">
                                {calculateCumulativeProduction(fieldDetail.production_data).oil.toFixed(1)}k tons
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Gas:</Typography>
                              <Typography variant="body2" color="info.main">
                                {calculateCumulativeProduction(fieldDetail.production_data).gas.toFixed(1)}k tons
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No data</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Wells by Type Card */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 140 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Wells by Type
                        </Typography>
                        {fieldDetail.wells && fieldDetail.wells.length > 0 ? (
                          <Box>
                            {Object.entries(getWellTypesCounts(fieldDetail.wells)).map(([type, count]) => (
                              <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                  {type}:
                                </Typography>
                                <Typography variant="body2" color="primary">
                                  {count}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Description Card */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 140 }}>
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Description
                        </Typography>
                        <Typography
                          variant="body2"
                          color={fieldDetail.description ? 'text.primary' : 'text.secondary'}
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {fieldDetail.description || 'No description'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Field Location Card */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FieldLocationCard
                      coordinate={fieldDetail.coordinate}
                      fieldName={fieldDetail.name}
                    />
                  </Grid>
                </Grid>

                {/* Field Productivity Chart */}
                {showFieldProductivityChart && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Field Productivity Data
                      </Typography>
                      <Box sx={{ height: 400, width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={fieldChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" />
                            <YAxis yAxisId="rates" />
                            {hasFieldWaterCut && (
                              <YAxis yAxisId="wc" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                            )}
                            <Tooltip
                              formatter={(value, name) => {
                                const numeric = Number(value);
                                if (name === 'Water cut') {
                                  return [`${numeric.toFixed(2)}%`, name];
                                }
                                if (name === 'Gas' || name === 'Oil' || name === 'Liquid') {
                                  return [formatThousandVolumeTooltip(numeric, SCENARIO_UNIT_TONS), name];
                                }
                                return [`${numeric.toFixed(2)}`, String(name)];
                              }}
                            />
                            <Legend />
                            {hasFieldOil && (
                              <Line
                                yAxisId="rates"
                                type="monotone"
                                dataKey="oil"
                                stroke="#8884d8"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                name="Oil"
                                connectNulls
                              />
                            )}
                            {hasFieldGas && (
                              <Line
                                yAxisId="rates"
                                type="monotone"
                                dataKey="gas"
                                stroke="#82ca9d"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                name="Gas"
                                connectNulls
                              />
                            )}
                            {hasFieldLiquid && (
                              <Line
                                yAxisId="rates"
                                type="monotone"
                                dataKey="liquid"
                                stroke="#4ecdc4"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                name="Liquid"
                                connectNulls
                              />
                            )}
                            {hasFieldWaterCut && (
                              <Line
                                yAxisId="wc"
                                type="monotone"
                                dataKey="waterCut"
                                stroke="#ffc658"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                name="Water cut"
                                connectNulls
                              />
                            )}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <LayersList
                      layers={fieldDetail.layers}
                      fieldId={fieldDetail.id}
                      wells={fieldDetail.wells}
                      canManage={fieldDetail.can_manage_resources}
                      onLayersUpdated={() => {
                        // Reload field details when layers are updated
                        console.log('onLayersUpdated called, reloading field details');
                        if (selectedDepositId) {
                          fieldsApi.getFieldDetail(selectedDepositId).then(setFieldDetail);
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <WellsList
                      wells={fieldDetail.wells}
                      layers={fieldDetail.layers}
                      fieldId={fieldDetail.id}
                      canManage={fieldDetail.can_manage_resources}
                      onWellsUpdated={() => {
                        // Reload field details when wells are updated
                        console.log('onWellsUpdated called, reloading field details');
                        if (selectedDepositId) {
                          fieldsApi.getFieldDetail(selectedDepositId).then(setFieldDetail);
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </>
      ) : !loading && !error && hasDeposits && !currentDeposit && (
        <>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            History
          </Typography>
          <Typography variant="body1" color="text.secondary" component="div" sx={{ mb: 3 }}>
            View and manage your calculation history
          </Typography>
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete field?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove &quot;{currentDeposit?.name ?? 'this field'}&quot; from your list. Related layers, wells, and scenarios will be preserved but hidden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteField} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
